import type { MessageCategory, Weekday, WorkflowSettings } from 'app/types/workflows'

export const WEEKDAYS: { value: Weekday; label: string; short: string }[] = [
  { value: `mon`, label: `Monday`, short: `M` },
  { value: `tue`, label: `Tuesday`, short: `T` },
  { value: `wed`, label: `Wednesday`, short: `W` },
  { value: `thu`, label: `Thursday`, short: `T` },
  { value: `fri`, label: `Friday`, short: `F` },
  { value: `sat`, label: `Saturday`, short: `S` },
  { value: `sun`, label: `Sunday`, short: `S` },
]

// The LEGAL texting window: 8:00 AM to 8:00 PM in the location's time zone. This is not a product
// preference — the API hard-refuses outbound SMS/RCS outside it (Message.beforeSave throws
// QUIET_HOURS when hour < 8 || hour >= 20 on the location's clock). Critically, a workflow send
// refused there is SKIPPED (`{skipped: true, reason: 'quiet_hours'}`), not held: only the
// workflow-level send window parks a send and resumes it in the morning. So the window below is
// load-bearing, and the editor clamps into these bounds rather than letting someone author a
// window whose overhang would silently drop texts.
export const LEGAL_SEND_START = `08:00`
export const LEGAL_SEND_END = `20:00`

/** Snap an HH:MM value into the legal texting window (string compare works for zero-padded HH:MM). */
export function clampToLegalSendWindow(value: string, fallback: string): string {
  if (!/^\d{2}:\d{2}$/.test(value)) return fallback
  if (value < LEGAL_SEND_START) return LEGAL_SEND_START
  if (value > LEGAL_SEND_END) return LEGAL_SEND_END
  return value
}

// Send window ON by default (UX audit 08-21, B1): the recall template's own design counts on quiet
// hours parking its small-hours "+1h after the due date" send until morning, and shipped with the
// window off, which meant ~1 AM sends silently refused. The default IS the legal window; daytime
// speed-to-lead replies are unaffected. Saved workflows keep their own settings.
export const DEFAULT_WORKFLOW_SETTINGS: WorkflowSettings = {
  sendTimes: {
    restrict: true,
    start: LEGAL_SEND_START,
    end: LEGAL_SEND_END,
    days: [`mon`, `tue`, `wed`, `thu`, `fri`, `sat`, `sun`],
    contactTimezone: true,
  },
  // Custom workflows default to MARKETING (decided 08-24, Patrick): a marketing send is refused by
  // the engine unless the contact's smsMarketingConsent is true, so the safe default is the gated
  // one. Templates declare their category deliberately; a practice can flip a custom workflow to
  // transactional when it genuinely is (appointments and care).
  category: `marketing`,
}

/** The category a workflow will actually send under. The engine reads an absent category as
 *  MARKETING (consent-gated) since api #588, so absent must read as that here too — anything else
 *  would promise sends the engine will skip. */
export function workflowCategory(settings?: WorkflowSettings): MessageCategory {
  return settings?.category ?? `marketing`
}

/** The category the ENGINE will send under, and whether the author's pick is still waiting on an
 *  admin. Only an admin's save applies a category (ops mutation, api #581), so a non-admin's
 *  "transactional" is a request rather than a state: every surface that states the category to an
 *  author reads it through here, or the builder promises sends the engine consent-gates. */
export function effectiveCategory(args: {
  canSetCategory?: boolean
  settings?: { category?: MessageCategory }
  serverCategory?: string | null
}): { category: MessageCategory; pending: boolean } {
  const chosen = args.settings?.category ?? `marketing`
  if (args.canSetCategory) return { category: chosen, pending: false }
  const applied = (args.serverCategory as MessageCategory | null | undefined) ?? `marketing`
  return { category: applied, pending: chosen !== applied }
}

/** Whether a save should push the texting category to the engine, and what to push. Only an admin's
 *  save can apply it (ops-schema mutation), and the engine reads an absent category as marketing —
 *  so a non-admin, or a choice the engine already holds, must not fire a call that would fail or
 *  no-op. Split out from the save handler so this decision is testable on its own. */
export function categorySyncPlan(args: {
  canSetCategory: boolean
  settings?: WorkflowSettings
  serverCategory?: string | null
}): { apply: false } | { apply: true; category: MessageCategory } {
  if (!args.canSetCategory) return { apply: false }
  const chosen = workflowCategory(args.settings)
  if (chosen === (args.serverCategory ?? `marketing`)) return { apply: false }
  return { apply: true, category: chosen }
}
